/**
 * Groovela progressive enhancements. Everything here is optional: the storefront works without it.
 *
 * 1. Product cards: on touch screens there is no hover, so a "See back" / "See front" button
 *    toggles between the first (front) and second (back) product image. On pointer devices the
 *    button only shows when it receives keyboard focus (see groovela.css).
 * 2. Product page gallery: labels the first two images FRONT and BACK when the theme setting is on.
 * 3. Marquee: duplicates the phrase track so the CSS animation loops seamlessly.
 */

const config = (() => {
  try {
    const el = document.getElementById('groovela-config');
    return el ? JSON.parse(el.textContent) : {};
  } catch (error) {
    return {};
  }
})();

const strings = {
  seeBack: config.seeBack || 'See back',
  seeFront: config.seeFront || 'See front',
  front: config.front || 'Front',
  back: config.back || 'Back',
};

/* ---------------------------------------------------------------------------
 * 1. Front / back toggle on product cards
 * ------------------------------------------------------------------------- */

const FLIP_ATTR = 'data-groovela-flip';

/**
 * @param {HTMLElement} gallery
 */
function enhanceCardGallery(gallery) {
  if (gallery.hasAttribute(FLIP_ATTR)) return;

  const slideshow = gallery.querySelector('slideshow-component');
  if (!slideshow) return;

  const slides = slideshow.querySelectorAll('slideshow-slide:not([hidden])');
  if (slides.length < 2) return;

  gallery.setAttribute(FLIP_ATTR, '');

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'groovela-flip';
  button.setAttribute('aria-pressed', 'false');
  button.textContent = strings.seeBack;

  let showingBack = false;

  const render = () => {
    button.setAttribute('aria-pressed', String(showingBack));
    button.textContent = showingBack ? strings.seeFront : strings.seeBack;
  };

  // Stop the card link from reacting to taps on the button (touch browsers can navigate on pointerup).
  ['pointerdown', 'pointerup', 'touchstart', 'touchend'].forEach((type) => {
    button.addEventListener(type, (event) => event.stopPropagation(), { passive: type.startsWith('touch') });
  });

  button.addEventListener('click', (event) => {
    // The whole card is a link; keep this click from navigating.
    event.preventDefault();
    event.stopPropagation();

    showingBack = !showingBack;
    const index = showingBack ? 1 : 0;

    if (typeof slideshow.select === 'function') {
      slideshow.select(index, undefined, { animate: false });
    }

    render();
  });

  // Keep the label in sync when the image changes another way (swipe, hover, arrows).
  slideshow.addEventListener('slideshow:select', (event) => {
    const index = event && event.detail ? event.detail.index : undefined;
    if (typeof index !== 'number') return;
    showingBack = index === 1;
    render();
  });

  // Desktop only: when the mouse leaves the card, the theme shows the front again.
  // Touch browsers also fire pointerleave right after every tap, which used to reset the state
  // before the click and left the card stuck on the back image.
  gallery.addEventListener('pointerleave', (event) => {
    if (event.pointerType !== 'mouse') return;
    if (showingBack) {
      showingBack = false;
      render();
    }
  });

  gallery.appendChild(button);
}

function enhanceAllCardGalleries(root = document) {
  if (config.flipButton === false) return;
  root.querySelectorAll('.card-gallery').forEach((gallery) => enhanceCardGallery(gallery));
}

/* ---------------------------------------------------------------------------
 * 2. FRONT / BACK labels on the product page gallery grid
 * ------------------------------------------------------------------------- */

function labelProductGallery(root = document) {
  if (config.galleryLabels === false) return;

  root.querySelectorAll('media-gallery .media-gallery__grid').forEach((grid) => {
    if (grid.hasAttribute('data-groovela-labelled')) return;

    const items = grid.querySelectorAll(':scope > li');
    if (items.length < 2) return;

    grid.setAttribute('data-groovela-labelled', '');

    [strings.front, strings.back].forEach((label, index) => {
      const item = items[index];
      if (!item || item.querySelector('.groovela-media-label')) return;

      const badge = document.createElement('span');
      badge.className = 'groovela-media-label';
      badge.textContent = label;
      item.appendChild(badge);
    });
  });
}

/* ---------------------------------------------------------------------------
 * 3. Marquee loop
 * ------------------------------------------------------------------------- */

function enhanceMarquees(root = document) {
  root.querySelectorAll('[data-groovela-marquee]').forEach((marquee) => {
    if (marquee.hasAttribute('data-groovela-marquee-ready')) return;

    const track = marquee.querySelector('[data-groovela-marquee-track]');
    if (!track || track.children.length === 0) return;

    // Remove any clones left by a previous render (theme editor re-renders).
    marquee.querySelectorAll('[data-groovela-marquee-clone]').forEach((clone) => clone.remove());

    const clone = track.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-groovela-marquee-clone', '');
    clone.removeAttribute('data-groovela-marquee-track');
    // The clone must not be selectable in the theme editor.
    clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));

    marquee.appendChild(clone);
    marquee.setAttribute('data-groovela-marquee-ready', '');
  });
}

/* ---------------------------------------------------------------------------
 * Boot + keep up with sections rendered later (infinite scroll, editor, filters)
 * ------------------------------------------------------------------------- */

function run(root) {
  enhanceAllCardGalleries(root);
  labelProductGallery(root);
  enhanceMarquees(root);
}

run(document);

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof HTMLElement) run(node);
    }
  }
});

observer.observe(document.body, { childList: true, subtree: true });

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target instanceof HTMLElement ? event.target : document;
  target.querySelectorAll('[data-groovela-marquee-ready]').forEach((el) => el.removeAttribute('data-groovela-marquee-ready'));
  run(target);
});
